import { useTranslation } from '@msdk/common'
import { GenericFieldValue, OperatorNames, OperatorType, ViewColumnDefinition } from '@msdk/definitions'
import { AddIcon, ButtonSecondary, IconSelector, styled } from '@msdk/ui'
import React, { useState } from 'react'

import { getFieldDefIncludeExternal } from '../../../utils'
import { useDynamicViewContext } from '../context'
import { conditionsToFilters, createFormNodeForFilter } from './conditions-to-filter'
import { createSimpleCondition } from './create-simple-condition'
import { Filter } from './Filter'
import { FilterItem } from './filter-item'
import { getDefaultOperatorName } from './get-operators'
import { OPERATORS } from './operators'

const Container = styled('div')`
  width: 552px;
  padding: 18px 10px 10px;
`

const ActionContainer = styled('div')`
  display: flex;
  flex-direction: row;
  margin-top: 10px;
  justify-content: space-between;
`

const AddButton = styled(ButtonSecondary)`
  border: 0;
  padding: 8px 12px;
`

const DeleteButton = styled(ButtonSecondary)`
  border: 0;
  padding: 8px 12px;
`

interface Props {
  columnDef: ViewColumnDefinition
}

export function FilterPanel({ columnDef }: Props) {
  const t = useTranslation()

  const {
    viewModel,
    additionalCondition,
    baseMetadata,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    filterColumns,
  } = useDynamicViewContext()

  const createFilter = (column: string) => {
    const fieldDef = getFieldDefIncludeExternal(viewModel, column, baseMetadata)

    return {
      id: crypto.randomUUID(),
      column,
      operatorType: getDefaultOperatorName(fieldDef.fieldDefinitionType) ?? '',
      node: createFormNodeForFilter(fieldDef, viewModel.name, null),
    }
  }

  const [filters, setFilters] = useState<FilterItem[]>(
    additionalCondition.conditions.length
      ? conditionsToFilters(viewModel, additionalCondition, baseMetadata)
      : [createFilter(columnDef.fieldName)],
  )

  const updateFilters = (id: string, column: string, operator?: OperatorNames, value?: GenericFieldValue) => {
    const fieldDef = getFieldDefIncludeExternal(viewModel, column, baseMetadata)
    const newFilters = [...filters]
    const operatorToUse = operator ?? getDefaultOperatorName(fieldDef.fieldDefinitionType)

    const toUpdate = newFilters.find((f) => f.id === id) as FilterItem
    toUpdate.column = column
    toUpdate.node = createFormNodeForFilter(fieldDef, viewModel.name, value, operatorToUse)
    toUpdate.operatorType = operatorToUse ?? ''

    const op = toUpdate.operatorType as OperatorNames

    if (toUpdate.column && op && (!OPERATORS[op].fieldVisible || (value && !value.isEmpty()))) {
      const condition = createSimpleCondition(
        toUpdate.id,
        toUpdate.column,
        OperatorType[toUpdate.operatorType as any] as any,
        toUpdate.node?.value?.data,
      )
      addColumnFilter(condition)
    } else {
      removeColumnFilter(toUpdate.id)
    }

    setFilters(newFilters)
  }

  const onValueChanged = (id: string, column: string, operator: OperatorNames, value: GenericFieldValue) => {
    updateFilters(id, column, operator, value)
  }

  const onColumnChanged = (id: string, column: string) => {
    updateFilters(id, column)
  }

  const onOperatorChanged = (id: string, column: string, operator: OperatorNames) => {
    updateFilters(id, column, operator)
  }

  const onDelete = (id: string) => {
    const newFilters = [...filters]
    const index = newFilters.findIndex((f) => f.id === id)
    newFilters.splice(index, 1)
    setFilters(newFilters)

    removeColumnFilter(id)
  }

  const addFilter = () => {
    if (filterColumns.length > 0) {
      const newFilter: FilterItem = createFilter(filterColumns[0].fieldName)
      const newFilters = [...filters]
      newFilters.push(newFilter)
      setFilters(newFilters)
    }
  }

  const clearFilters = () => {
    let newFilter = [{ id: crypto.randomUUID(), column: '' }]
    if (filterColumns.length > 0) {
      newFilter = [createFilter(filterColumns[0].fieldName)]
    }

    setFilters(newFilter)
    removeAllColumnFilter()
  }

  return (
    <Container>
      <div>
        {filters.map((f) => (
          <Filter
            id={f.id}
            key={f.id}
            onValueChange={onValueChanged}
            onColumnChange={onColumnChanged}
            onOperatorChange={onOperatorChanged}
            onDelete={onDelete}
            column={f.column}
            operator={f.operatorType}
            node={f.node}
          />
        ))}
      </div>
      <ActionContainer>
        <AddButton onClick={addFilter}>
          <AddIcon />
          {t('CMPS_ADD_FILTER', 'Add')}
        </AddButton>
        <DeleteButton onClick={clearFilters}>
          <IconSelector iconName="DeleteOutlineOutlined" />
          {t('CMPS_CLEAR_FILTER', 'Clear filter')}
        </DeleteButton>
      </ActionContainer>
    </Container>
  )
}
